import React, { useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Flex } from "antd";
import { get } from "lodash";
import { FieldError, useForm } from "react-hook-form/dist/index.ie11";
import { useTranslation } from "react-i18next";

import { useSetLocationData } from "~/hooks/locationData";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import { TSelectItem } from "~/model/common";
import { selectStatus } from "~/pages/Editor/EditorReports/ui/CatalogsOfReports/config/options";
import { SelectOption } from "~/pages/Editor/type";
import {
  IndicatorReport,
  useAddIndicatorsToReportMutation,
  useChangeIndicatorReportMutation,
  useCreateIndicatorReportMutation,
  useDeleteIndicatorReportMutation,
  useGetIndicatorReportLazyQuery,
  useRestoreIndicatorReportMutation,
  useUpsertDealerFieldsToReportMutation,
} from "~/schemas/generated/graphql";
import { useNotify } from "~/shared/hooks/useNotify";
import { Field } from "~/shared/ui/Field";
import { IndicatorGroupStatus } from "~/types/IndicatorGroupStatus";

import ReportDealerFieldsForm from "./ReportDealerFieldsForm";
import ReportIndicatorsForm from "./ReportIndicatorsForm";
import { getYupSchemaForManageReportForm } from "./lib";

export type Report = {
  name: string;
  status?: TSelectItem<IndicatorGroupStatus>;
  indicators: SelectOption[];
  dealerFields: SelectOption[];
};

interface ManageReportFormProps {
  refetch: () => void;
  disabled: boolean;
  formId: string;
}

const ManageReportForm = ({
  refetch,
  disabled,
  formId,
}: ManageReportFormProps) => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter(true);
  const { reportId, openModal, groupId } = useURLParamsGetter<{
    reportId: string;
    openModal: string;
    groupId: string;
  }>();

  const setData = useSetLocationData(true);

  const isAddModalOpen = openModal === "Add";
  const isEditModalOpen = openModal === "Edit";

  const statuses = selectStatus(t);

  const [getCurrentReportData, currentReportDataState] =
    useGetIndicatorReportLazyQuery({ fetchPolicy: "no-cache" });

  const currentReportData: IndicatorReport = get(
    currentReportDataState,
    "data.IndicatorGroupReportGetter.getIndicatorReport",
  );

  const { notifySuccess, notifyError } = useNotify();

  const [createReportName] = useCreateIndicatorReportMutation({
    fetchPolicy: "no-cache",
  });

  const [deleteReportTemplate] = useDeleteIndicatorReportMutation({
    fetchPolicy: "no-cache",
    onCompleted: () => {
      notifySuccess('Отчет помечен как "Удаленный"');
    },
  });

  const [restoreReportTemplate] = useRestoreIndicatorReportMutation({
    fetchPolicy: "no-cache",
    onCompleted: () => {
      notifySuccess('Отчет помечен как "Активный"');
    },
  });

  const [addIndicatorsToReport] = useAddIndicatorsToReportMutation({
    fetchPolicy: "no-cache",
    onCompleted: () => {
      notifySuccess(t("common.сhange_was_successful"));
    },
  });

  const [upsertDealerFieldsToReport] = useUpsertDealerFieldsToReportMutation({
    fetchPolicy: "no-cache",
    onCompleted: () => {
      notifySuccess(t("common.сhange_was_successful"));
    },
  });
  /**
   * запрос на изменение имени шаблона отчета
   */
  const [changeReportName] = useChangeIndicatorReportMutation({
    fetchPolicy: "no-cache",
  });

  const { control, handleSubmit, errors, setValue } = useForm<Report>({
    defaultValues: { name: "", indicators: [], dealerFields: [] },
    resolver: yupResolver(getYupSchemaForManageReportForm(t)),
  });

  /**
   * у массивов (indicators, dealerFields) errors имеет другую форму,
   * поэтому приводим к плоской карте ошибок, как в исходной форме
   */
  const formErrors = errors as { [key: string]: FieldError };

  const onHandleSubmit = async (data: Report) => {
    if (isAddModalOpen) {
      try {
        const { name, indicators } = data;

        /**
         * отправляем первый запрос на создание отчета (отправка имени отчет и получение его ид)
         */
        const firstResponse = await createReportName({
          variables: {
            GroupId: groupId,
            nameRu: name,
          },
        });

        const reportId = get(
          firstResponse,
          "data.IndicatorGroupReportSetter.createIndicatorReport.id",
        );
        if (reportId) {
          const indicatorsIds = indicators.map((el) => el.value);
          /**
           * отправляем второй запрос с полученным ид от первого запроса
           */
          await addIndicatorsToReport({
            variables: {
              ReportId: reportId,
              IndicatorIds: indicatorsIds,
            },
          });
          /**
           * закрываем модалку и делаем рефетч
           */
          setParams({ openModal: undefined, reportId: undefined });
          await refetch();
        }
      } catch (error) {
        const cleanedErrorMessage = error.message.replace(
          "GraphQL error: ",
          "",
        );
        notifyError(cleanedErrorMessage);
      }
    }

    if (isEditModalOpen) {
      try {
        const { name, status, indicators, dealerFields } = data;
        const indicatorsIds = indicators.map((el) => el.value);

        await changeReportName({ variables: { id: reportId, nameRu: name } });

        await addIndicatorsToReport({
          variables: {
            ReportId: reportId,
            IndicatorIds: indicatorsIds,
          },
        });

        await upsertDealerFieldsToReport({
          variables: {
            reportId: String(reportId),
            dealerFieldsIds: dealerFields.map((el) => el.value),
          },
        });

        if (String(status?.value) !== String(currentReportData.status)) {
          const variables = {
            id: currentReportData.id,
            GroupId: currentReportData.GroupId,
          };
          switch (status?.value) {
            case IndicatorGroupStatus.Active:
              await restoreReportTemplate({ variables });
              break;
            case IndicatorGroupStatus.Removed:
              await deleteReportTemplate({ variables });
              break;
          }
        }

        /**
         * закрываем модалку и делаем рефетч
         */
        setData("openModal", "", "reportId", "");
        await refetch();
      } catch (error) {
        const cleanedErrorMessage = error.message.replace(
          "GraphQL error: ",
          "",
        );
        notifyError(cleanedErrorMessage);
      }
    }
  };

  useEffect(() => {
    if (isEditModalOpen && reportId) {
      getCurrentReportData({ variables: { ReportId: reportId } });
    }
  }, [openModal]);

  useEffect(() => {
    if (currentReportData) {
      const { nameRu, Indicators, Fields } = currentReportData;
      const transformedIndicators = Indicators.map((el) => ({
        label: `(${el.id}) ${el.nameRu}`,
        value: el.id,
      }));

      const transformedDealerFields = Fields.map((el) => ({
        label: `(${el.id}) ${el.fieldRuDescription}`,
        value: el.id,
      }));

      setValue("name", nameRu);
      setValue("status", {
        value: currentReportData.status,
        label: t(`reports_list.${currentReportData.status}`),
      });
      setValue("indicators", transformedIndicators);
      setValue("dealerFields", transformedDealerFields);
    }
  }, [currentReportDataState.loading]);

  return (
    <form id={formId} onSubmit={handleSubmit(onHandleSubmit)}>
      <Flex vertical gap={10}>
        <Field
          type="input"
          name="name"
          control={control}
          label={t("report_list.report_name")}
          error={errors["name"]?.message}
          disabled={disabled}
        />
        {isEditModalOpen && (
          <Field
            type="select"
            name="status"
            control={control}
            label={t("reports_list.status")}
            options={statuses}
            labelInValue
          />
        )}
        <ReportIndicatorsForm
          control={control}
          error={formErrors["indicators"]?.message}
          disabled={disabled}
        />
        <ReportDealerFieldsForm
          control={control}
          setValue={setValue}
          error={formErrors["dealerFields"]?.message}
          disabled={disabled}
        />
      </Flex>
    </form>
  );
};

export default ManageReportForm;
